import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, Shield, RefreshCw, Check, X, FileText, TrendingUp, Eye, Settings } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import toast from 'react-hot-toast';
import Header from '../components/Header';
import CurrencyIcon from '../components/CurrencyIcon';
import { FadeUp, StaggerContainer, StaggerItem } from '../components/Motion';
import AdminSettingsPanel from '../components/AdminSettingsPanel';
import AdminSupport from '../components/AdminSupport';
import AdminP2PDetail from '../components/AdminP2PDetail';
import AdminRequestDetail from '../components/AdminRequestDetail';
import api from '../utils/api';

const Admin = () => {
  const { t } = useLanguage();
  const [users, setUsers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [p2pOrders, setP2pOrders] = useState([]);
  const [kycSubs, setKycSubs] = useState([]);
  const [stats, setStats] = useState({});
  const [activeTab, setActiveTab] = useState('requests');
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [selP2P, setSelP2P] = useState(null);
  const [selReq, setSelReq] = useState(null);
  const [supportUnread, setSupportUnread] = useState(0);

  useEffect(() => {
    const check = () => api.get('/admin/support/unread').then((r) => setSupportUnread(r.data.unread)).catch(() => {});
    check();
    const iv = setInterval(check, 10000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [requestsRes, usersRes, statsRes] = await Promise.all([
        api.get('/admin/exchange-requests'), api.get('/admin/users'), api.get('/admin/stats')
      ]);
      setRequests(requestsRes.data.requests || []);
      setUsers(usersRes.data.users || []);
      setStats(statsRes.data || {});
      try { const r = await api.get('/admin/p2p/orders'); setP2pOrders(r.data.orders || []); } catch {}
      try { const r = await api.get('/admin/kyc'); setKycSubs(r.data.submissions || []); } catch {}
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally { setLoading(false); }
  };

  const downloadFile = async (url, filename) => {
    try {
      const res = await api.get(url, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = blobUrl; a.download = filename; a.click();
      URL.revokeObjectURL(blobUrl);
    } catch { toast.error('Fichier indisponible'); }
  };

  const viewFile = async (url) => {
    try {
      const res = await api.get(url, { responseType: 'blob' });
      window.open(URL.createObjectURL(res.data), '_blank');
    } catch { toast.error('Fichier indisponible'); }
  };

  const updateP2P = async (id, status) => {
    try {
      await api.put(`/admin/p2p/orders/${id}/status`, { status });
      toast.success(`Ordre P2P: ${status}`); fetchData();
    } catch { toast.error('Erreur'); }
  };

  const updateKyc = async (id, status) => {
    try {
      await api.put(`/admin/kyc/${id}`, { status });
      toast.success(`KYC: ${status}`); fetchData();
    } catch { toast.error('Erreur'); }
  };

  const updateRequestStatus = async (requestId, newStatus) => {
    try {
      await api.put(`/admin/exchange-requests/${requestId}`, { status: newStatus });
      toast.success(`Demande mise à jour: ${newStatus}`);
      fetchData();
    } catch { toast.error('Erreur lors de la mise à jour'); }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending_payment: { cls: 'bg-amber-100 text-amber-700', label: t.admin?.pending || 'En attente' },
      payment_confirming: { cls: 'bg-blue-100 text-blue-700', label: t.admin?.confirming || 'À confirmer' },
      processing: { cls: 'bg-violet-100 text-violet-700', label: t.admin?.processing || 'En cours' },
      completed: { cls: 'bg-emerald-100 text-emerald-700', label: t.admin?.completed || 'Terminé' },
      cancelled: { cls: 'bg-gray-100 text-gray-500', label: t.admin?.cancelled || 'Annulé' },
      rejected: { cls: 'bg-rose-100 text-rose-700', label: t.admin?.rejected || 'Rejeté' }
    };
    const badge = badges[status] || badges.pending_payment;
    return <span className={`px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap ${badge.cls}`}>{badge.label}</span>;
  };

  const filteredRequests = filterStatus === 'all' ? requests : requests.filter(r => r.status === filterStatus);

  return (
    <div className="min-h-screen bg-[#F5F6F7]">
      <Header />

      {/* Black band */}
      <div className="bg-black px-6 pt-10 pb-24">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex items-center space-x-4">
            <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center">
              <Shield className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-white font-extrabold text-4xl tracking-tight" style={{ fontFamily: 'Manrope' }} data-testid="admin-title">{t.admin?.title || 'Panel Admin'}</h1>
              <p className="text-white/50">{t.admin?.subtitle || "Gestion des demandes d'échange"}</p>
            </div>
          </motion.div>
          <button onClick={fetchData}
            className="btn-pill-ghost-dark flex items-center space-x-2 px-6 py-3 text-sm" data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /><span>{t.admin?.refresh || 'Actualiser'}</span>
          </button>
        </div>
      </div>

      <div className="px-6 pb-16 -mt-14" data-testid="admin-container">
        <div className="max-w-7xl mx-auto">
          <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="stats-cards">
            {[
              { icon: Users, label: t.admin?.users || 'Utilisateurs', value: stats.total_users || 0 },
              { icon: FileText, label: t.admin?.pendingRequests || 'En attente', value: stats.pending_requests || 0 },
              { icon: Check, label: t.admin?.completedRequests || 'Complétées', value: stats.completed_requests || 0 },
              { icon: TrendingUp, label: t.admin?.totalVolume || 'Volume total', value: `$${(stats.total_volume || 0).toLocaleString()}` },
            ].map((s, idx) => {
              const Icon = s.icon;
              return (
                <StaggerItem key={idx} className="bg-white rounded-3xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.05)]">
                  <div className="w-11 h-11 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-gray-900" strokeWidth={1.8} />
                  </div>
                  <div className="text-sm text-gray-400 mb-1">{s.label}</div>
                  <div className="text-3xl font-extrabold text-gray-900 font-mono-data tracking-tight">{s.value}</div>
                </StaggerItem>
              );
            })}
          </StaggerContainer>

          <div className="bg-white rounded-full p-1.5 mb-8 inline-flex shadow-[0_4px_20px_rgb(0,0,0,0.05)]" data-testid="admin-tabs">
            <button onClick={() => setActiveTab('requests')}
              className={`px-8 py-3 rounded-full font-bold text-sm transition-colors ${activeTab === 'requests' ? 'bg-[#191C1F] text-white' : 'text-gray-500 hover:text-gray-900'}`} data-testid="tab-requests">
              {t.admin?.exchangeRequests || "Demandes d'échange"}
            </button>
            <button onClick={() => setActiveTab('users')}
              className={`px-8 py-3 rounded-full font-bold text-sm transition-colors ${activeTab === 'users' ? 'bg-[#191C1F] text-white' : 'text-gray-500 hover:text-gray-900'}`} data-testid="tab-users">
              {t.admin?.usersTab || 'Utilisateurs'}
            </button>
            <button onClick={() => setActiveTab('p2p')}
              className={`px-8 py-3 rounded-full font-bold text-sm transition-colors ${activeTab === 'p2p' ? 'bg-[#191C1F] text-white' : 'text-gray-500 hover:text-gray-900'}`} data-testid="tab-p2p">
              Trading P2P ({p2pOrders.length})
            </button>
            <button onClick={() => setActiveTab('kyc')}
              className={`px-8 py-3 rounded-full font-bold text-sm transition-colors ${activeTab === 'kyc' ? 'bg-[#191C1F] text-white' : 'text-gray-500 hover:text-gray-900'}`} data-testid="tab-kyc">
              KYC ({kycSubs.length})
            </button>
            <button onClick={() => setActiveTab('support')}
              className={`relative px-8 py-3 rounded-full font-bold text-sm transition-colors ${activeTab === 'support' ? 'bg-[#191C1F] text-white' : 'text-gray-500 hover:text-gray-900'}`} data-testid="tab-support">
              Support
              {supportUnread > 0 && <span className="absolute -top-1 -right-1 w-5 h-5 bg-rose-500 text-white text-[10px] font-extrabold rounded-full flex items-center justify-center" data-testid="support-unread-badge">{supportUnread > 9 ? '9+' : supportUnread}</span>}
            </button>
            <button onClick={() => setActiveTab('settings')}
              className={`px-8 py-3 rounded-full font-bold text-sm transition-colors flex items-center space-x-1.5 ${activeTab === 'settings' ? 'bg-[#191C1F] text-white' : 'text-gray-500 hover:text-gray-900'}`} data-testid="tab-settings">
              <Settings className="w-4 h-4" /><span>Paramètres</span>
            </button>
          </div>

          {activeTab === 'settings' && <AdminSettingsPanel />}
          {activeTab === 'support' && <AdminSupport />}

          {activeTab === 'p2p' && (
            <FadeUp className="bg-white rounded-3xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.05)]" data-testid="p2p-admin-section">
              <h2 className="font-extrabold text-2xl text-gray-900 mb-6 tracking-tight" style={{ fontFamily: 'Manrope' }}>Trading Direct P2P</h2>
              {p2pOrders.length === 0 ? <div className="text-center py-10 text-gray-400">Aucun ordre P2P</div> : (
                <div className="space-y-3">
                  {p2pOrders.map((o, idx) => (
                    <div key={o.id} className="bg-gray-50 rounded-2xl p-5" data-testid={`p2p-admin-row-${idx}`}>
                      <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="flex items-center space-x-3">
                          <CurrencyIcon symbol={o.crypto} size={28} />
                          <div>
                            <div className="font-bold text-gray-900 font-mono-data text-sm">{o.side.toUpperCase()} · {o.amount_crypto} {o.crypto} ({o.network}) → {o.amount_fiat?.toFixed(2)} {o.fiat_currency}</div>
                            <div className="text-xs text-gray-400">Maker: {o.maker_email} {o.taker_email ? `· Taker: ${o.taker_email}` : ''}</div>
                            {o.bank_card && <div className="text-xs text-gray-400 font-mono-data">CB vendeur: {o.bank_card}</div>}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 flex-wrap gap-2">
                          <span className={`text-[10px] font-bold uppercase px-2.5 py-1 rounded-full whitespace-nowrap ${o.counterparty_type === 'company' ? 'gradient-pink-violet text-white' : o.taker_email ? 'bg-emerald-100 text-emerald-700' : o.status === 'open' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-400'}`}>
                            {o.counterparty_type === 'company' ? 'alphaswapp' : o.taker_email ? 'P2P' : o.status === 'open' ? 'ouvert' : '—'}
                          </span>
                          <span className="text-xs font-bold px-3 py-1.5 rounded-full bg-gray-200 text-gray-600">{o.status}</span>
                          <button onClick={() => setSelP2P(o)}
                            className="btn-pill-dark px-4 py-2 text-xs flex items-center space-x-1.5 hover:scale-105 transition-transform" data-testid={`p2p-details-${idx}`}>
                            <Eye className="w-3.5 h-3.5" /><span>Détails</span>
                          </button>
                          {o.status === 'escrow_pending' && (
                            <button onClick={() => updateP2P(o.id, 'escrow_received')} className="btn-pill-dark px-4 py-2 text-xs" data-testid={`p2p-escrow-ok-${idx}`}>Séquestre reçu ✓</button>
                          )}
                          {o.status === 'fiat_sent' && (
                            <>
                              {o.receipt && <button onClick={() => viewFile(`/admin/p2p/orders/${o.id}/receipt`)} className="btn-pill-ghost px-4 py-2 text-xs" data-testid={`p2p-receipt-${idx}`}>Voir reçu</button>}
                              <button onClick={() => updateP2P(o.id, 'completed')} className="bg-emerald-600 text-white rounded-full px-4 py-2 text-xs font-bold" data-testid={`p2p-complete-${idx}`}>Libérer la crypto ✓</button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </FadeUp>
          )}

          {activeTab === 'kyc' && (
            <FadeUp className="bg-white rounded-3xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.05)]" data-testid="kyc-admin-section">
              <h2 className="font-extrabold text-2xl text-gray-900 mb-6 tracking-tight" style={{ fontFamily: 'Manrope' }}>Vérifications KYC</h2>
              {kycSubs.length === 0 ? <div className="text-center py-10 text-gray-400">Aucune soumission KYC</div> : (
                <div className="space-y-3">
                  {kycSubs.map((k, idx) => (
                    <div key={k.id} className="bg-gray-50 rounded-2xl p-5" data-testid={`kyc-row-${idx}`}>
                      <div className="flex items-center justify-between flex-wrap gap-3">
                        <div>
                          <div className="font-bold text-gray-900">{k.first_name} {k.last_name} <span className="text-gray-400 font-normal text-sm">· {k.user_email}</span></div>
                          <div className="text-xs text-gray-400">{k.id_type} {k.id_number} · {k.nationality} · {new Date(k.submitted_at).toLocaleString('fr-FR')}</div>
                          <div className="flex items-center space-x-2 mt-2 flex-wrap gap-1">
                            {(k.documents || []).map((d) => (
                              <button key={d.id} onClick={() => viewFile(`/admin/kyc-file/${k.id}/${d.id}`)}
                                className="text-xs font-semibold bg-white border border-gray-200 rounded-full px-3 py-1.5 hover:border-gray-400" data-testid={`kyc-doc-${d.id}`}>
                                📄 {d.doc_type}
                              </button>
                            ))}
                            {(!k.documents || k.documents.length === 0) && <span className="text-xs text-gray-300">Aucun document</span>}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${k.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : k.status === 'rejected' ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'}`}>{k.status}</span>
                          {k.status === 'pending' && (
                            <>
                              <button onClick={() => updateKyc(k.id, 'approved')} className="p-2 bg-emerald-100 text-emerald-600 rounded-full hover:bg-emerald-200" data-testid={`kyc-approve-${idx}`}><Check className="w-4 h-4" /></button>
                              <button onClick={() => updateKyc(k.id, 'rejected')} className="p-2 bg-rose-100 text-rose-600 rounded-full hover:bg-rose-200" data-testid={`kyc-reject-${idx}`}><X className="w-4 h-4" /></button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </FadeUp>
          )}

          {activeTab === 'requests' && (
            <FadeUp className="bg-white rounded-3xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.05)]" data-testid="requests-section">
              <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
                <h2 className="font-extrabold text-2xl text-gray-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>{t.admin?.exchangeRequests || "Demandes d'échange"} ({filteredRequests.length})</h2>
                <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
                  className="bg-[#F5F6F7] text-gray-900 px-5 py-2.5 rounded-full outline-none font-semibold text-sm cursor-pointer" data-testid="filter-status">
                  <option value="all">{t.admin?.allStatuses || 'Tous les statuts'}</option>
                  <option value="pending_payment">{t.admin?.pending || 'En attente'}</option>
                  <option value="payment_confirming">{t.admin?.confirming || 'À confirmer'}</option>
                  <option value="processing">{t.admin?.processing || 'En cours'}</option>
                  <option value="completed">{t.admin?.completed || 'Terminé'}</option>
                  <option value="cancelled">{t.admin?.cancelled || 'Annulé'}</option>
                  <option value="rejected">{t.admin?.rejected || 'Rejeté'}</option>
                </select>
              </div>

              {filteredRequests.length === 0 ? (
                <div className="text-center py-12 text-gray-300">
                  <FileText className="w-16 h-16 mx-auto mb-4 opacity-30" /><p>{t.admin?.noRequests || 'Aucune demande trouvée'}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-100">
                        {[t.admin?.requestNumber || 'N° Demande', t.admin?.email || 'Email', t.admin?.gives || 'Donne', t.admin?.receives || 'Reçoit', t.admin?.status || 'Statut', t.admin?.date || 'Date', t.admin?.actions || 'Actions'].map(h => (
                          <th key={h} className="text-left py-4 px-4 text-gray-400 font-semibold text-sm">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRequests.map((req, idx) => (
                        <tr key={req.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors" data-testid={`request-row-${idx}`}>
                          <td className="py-4 px-4 font-mono-data text-gray-900 font-bold">#{req.request_number}</td>
                          <td className="py-4 px-4 text-gray-600">{req.email}</td>
                          <td className="py-4 px-4">
                            <div className="flex items-center space-x-2">
                              {req.give_method?.symbol && <CurrencyIcon symbol={req.give_method.symbol} size={20} />}
                              <span className="font-bold text-gray-900 font-mono-data">{req.give_amount}</span>
                              <span className="text-gray-400 text-sm">{req.give_method?.symbol || req.give_method?.name}</span>
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <div className="flex items-center space-x-2">
                              {req.receive_method?.symbol && <CurrencyIcon symbol={req.receive_method.symbol} size={20} />}
                              <span className="font-bold text-gray-900 font-mono-data">{req.receive_amount}</span>
                              <span className="text-gray-400 text-sm">{req.receive_method?.symbol || req.receive_method?.name}</span>
                            </div>
                          </td>
                          <td className="py-4 px-4">{getStatusBadge(req.status)}</td>
                          <td className="py-4 px-4 text-gray-400 text-sm">{new Date(req.created_at).toLocaleString('fr-FR')}</td>
                          <td className="py-4 px-4">
                            <div className="flex items-center space-x-2">
                              <button onClick={() => setSelReq(req)} className="p-2 bg-gray-900 text-white rounded-full hover:bg-gray-700 hover:scale-110 transition-all" title="Détails & modification" data-testid={`req-details-${idx}`}>
                                <Eye className="w-4 h-4" />
                              </button>
                              {req.status === 'payment_confirming' && (
                                <>
                                  <button onClick={() => updateRequestStatus(req.id, 'completed')} className="p-2 bg-emerald-100 text-emerald-600 rounded-full hover:bg-emerald-200 transition-colors" title="Valider" data-testid={`approve-btn-${idx}`}>
                                    <Check className="w-4 h-4" />
                                  </button>
                                  <button onClick={() => updateRequestStatus(req.id, 'rejected')} className="p-2 bg-rose-100 text-rose-600 rounded-full hover:bg-rose-200 transition-colors" title="Rejeter" data-testid={`reject-btn-${idx}`}>
                                    <X className="w-4 h-4" />
                                  </button>
                                </>
                              )}
                              {req.status === 'completed' && (
                                <button onClick={() => downloadFile(`/admin/exchange-requests/${req.id}/receipt`, `recu-${req.request_number}.pdf`)}
                                  className="px-3 py-2 bg-gray-900 text-white rounded-full text-xs font-bold hover:bg-gray-700 transition-colors" title="Télécharger le reçu PDF" data-testid={`receipt-btn-${idx}`}>
                                  PDF
                                </button>
                              )}
                              {req.status === 'pending_payment' && (
                                <button onClick={() => updateRequestStatus(req.id, 'cancelled')} className="p-2 bg-gray-100 text-gray-500 rounded-full hover:bg-gray-200 transition-colors" title="Annuler" data-testid={`cancel-btn-${idx}`}>
                                  <X className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </FadeUp>
          )}

          {activeTab === 'users' && (
            <FadeUp className="bg-white rounded-3xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.05)]" data-testid="users-section">
              <h2 className="font-extrabold text-2xl text-gray-900 mb-6 tracking-tight" style={{ fontFamily: 'Manrope' }}>{t.admin?.usersTab || 'Utilisateurs'} ({users.length})</h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-100">
                      {[t.admin?.email || 'Email', t.admin?.name || 'Nom', t.admin?.role || 'Rôle', t.admin?.level || 'Niveau', t.admin?.volume || 'Volume', t.admin?.cashback || 'Cashback', t.admin?.registeredOn || 'Inscrit le'].map(h => (
                        <th key={h} className="text-left py-4 px-4 text-gray-400 font-semibold text-sm">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u, idx) => (
                      <tr key={u.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors" data-testid={`user-row-${idx}`}>
                        <td className="py-4 px-4 font-semibold text-gray-900">{u.email}</td>
                        <td className="py-4 px-4 text-gray-500">{u.name || '-'}</td>
                        <td className="py-4 px-4"><span className={`px-3 py-1 rounded-full text-xs font-bold ${u.role === 'admin' ? 'bg-rose-100 text-rose-700' : 'bg-gray-100 text-gray-600'}`}>{u.role}</span></td>
                        <td className="py-4 px-4"><span className="px-3 py-1 rounded-full text-xs font-bold bg-violet-100 text-violet-700">Lvl {u.user_level || 1}</span></td>
                        <td className="py-4 px-4 font-mono-data text-gray-900">${(u.total_volume || 0).toFixed(2)}</td>
                        <td className="py-4 px-4 font-mono-data text-emerald-600">${(u.cashback_earned || 0).toFixed(2)}</td>
                        <td className="py-4 px-4 text-gray-400 text-sm">{new Date(u.created_at).toLocaleDateString('fr-FR')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </FadeUp>
          )}
        </div>
      </div>

      {selP2P && <AdminP2PDetail order={selP2P} onClose={() => setSelP2P(null)} onRefresh={fetchData} />}
      {selReq && <AdminRequestDetail request={selReq} onClose={() => setSelReq(null)} onRefresh={fetchData} />}
    </div>
  );
};

export default Admin;
